import { Component, signal, computed, OnInit, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { rxResource } from '@angular/core/rxjs-interop';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { of, debounceTime, distinctUntilChanged, switchMap } from 'rxjs';
import {
  EventsService,
  UserEvent,
  EventType,
  EventVisibility,
  EventAccess,
  StartType,
  CompilationMilestone,
  EventAdmin
} from '../../../services/events.service';
import {
  QuestMetadata,
  Obelisk,
  Compilation,
  QuestsCrudService,
  ExplorationService,
  CompilationsDataService,
  User,
  PageHeaderComponent,
  PageHeaderButton
} from 'quest-atlas-angular-components';
import { UserService } from '../../../services/user.service';
import { UserListItemComponent } from '../../../common-components/user-list-item/user-list-item.component';
import { ModalComponent, ModalButton } from '../../../common-components/modal/modal.component';

@Component({
  selector: 'app-event-editor',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslocoModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    UserListItemComponent,
    ModalComponent,
    PageHeaderComponent
  ],
  templateUrl: './event-editor.component.html',
  styleUrl: './event-editor.component.scss'
})
export class EventEditorComponent implements OnInit {
  eventId = signal<number | string | null>(null);
  isNew = computed(() => this.eventId() === null || this.eventId() === 'new');

  event = rxResource({
    params: () => ({ id: this.eventId() }),
    stream: ({ params }) => {
      return params.id && params.id !== 'new'
        ? this.eventsService.getEvent$(params.id as number)
        : of(null);
    }
  });

  eventForm: FormGroup;
  milestones = signal<CompilationMilestone[]>([]);

  selectedQuest = signal<QuestMetadata | null>(null);
  selectedObelisk = signal<Obelisk | null>(null);
  selectedCompilation = signal<Compilation | null>(null);

  // Content search modals
  showContentSearchModal = signal(false);
  contentSearchType = signal<'quest' | 'compilation' | null>(null);
  contentSearchControl = new FormControl('', { nonNullable: true });
  contentSearchResults = signal<(QuestMetadata | Compilation)[]>([]);
  isContentSearching = signal(false);

  // Admin management
  adminsList = signal<EventAdmin[]>([]);
  showAddAdminModal = signal(false);
  userSearchControl = new FormControl('', { nonNullable: true });
  searchResults = signal<User[]>([]);
  isSearching = signal(false);

  isSaving = signal(false);

  // Toolbar buttons configuration
  toolbarButtons = computed<PageHeaderButton[]>(() => [
    {
      label: 'cancel',
      onClick: () => this.cancel(),
      type: 'secondary'
    },
    {
      label: this.isSaving() ? 'saving' : 'save',
      onClick: () => this.save(),
      disabled: () => this.isSaving(),
      type: 'primary'
    }
  ]);

  // Enums for templates
  EventType = EventType;
  EventVisibility = EventVisibility;
  EventAccess = EventAccess;

  visibilityOptions = [
    { value: EventVisibility.Public, label: 'public' },
    { value: EventVisibility.Contacts, label: 'contacts' },
    { value: EventVisibility.Private, label: 'private' }
  ];

  accessOptions = [
    { value: EventAccess.Free, label: 'free' },
    { value: EventAccess.Approval, label: 'approvalRequired' }
  ];

  private eventDataLoaded = signal(false);

  addAdminModalButtons: ModalButton[] = [
    {
      type: 'secondary',
      text: this.transloco.translate('close'),
      onClick: () => this.closeAddAdminModal()
    }
  ];

  contentSearchModalButtons: ModalButton[] = [
    {
      type: 'secondary',
      text: this.transloco.translate('close'),
      onClick: () => this.closeContentSearchModal()
    }
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private eventsService: EventsService,
    private questsService: QuestsCrudService,
    private explorationService: ExplorationService,
    private compilationsService: CompilationsDataService,
    private userService: UserService,
    private transloco: TranslocoService,
    private snackBar: MatSnackBar
  ) {
    this.eventForm = this.createEventForm();

    // Watch for event data to load and populate form
    effect(() => {
      const eventData = this.event.value();
      if (eventData && !this.eventDataLoaded()) {
        this.loadEventData(eventData);
        this.eventDataLoaded.set(true);
      }
    });

    // Set up user search with debounce
    effect(() => {
      this.userSearchControl.valueChanges
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          switchMap(searchTerm => {
            if (searchTerm.length < 2) {
              this.searchResults.set([]);
              return of(null);
            }
            this.isSearching.set(true);
            return this.userService.findUser$(searchTerm);
          })
        )
        .subscribe({
          next: (user) => {
            this.isSearching.set(false);
            if (user) {
              this.searchResults.set([user]);
            }
          },
          error: () => {
            this.isSearching.set(false);
            this.searchResults.set([]);
          }
        });
    });

    // Set up content search with debounce
    effect(() => {
      this.contentSearchControl.valueChanges
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          switchMap(searchTerm => {
            if (searchTerm.length < 2) {
              this.contentSearchResults.set([]);
              return of([]);
            }
            this.isContentSearching.set(true);
            const type = this.contentSearchType();

            if (type === 'quest') {
              // Use loadQuests with correct parameters
              return this.questsService.loadQuests(
                undefined, // location
                searchTerm, // search
                false, // liteVersion
                { access: { public: true, shared: true, private: true } }, // filters
                { selectDrafts: false } // options
              );
            } else if (type === 'compilation') {
              // Use getCompilations$ with search parameter
              return this.compilationsService.getCompilations$(
                'all', // mode
                undefined, // location
                undefined, // pagination
                searchTerm // search
              );
            }
            return of([]);
          })
        )
        .subscribe({
          next: (results) => {
            this.isContentSearching.set(false);
            this.contentSearchResults.set(results);
          },
          error: () => {
            this.isContentSearching.set(false);
            this.contentSearchResults.set([]);
          }
        });
    });
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const id = params['id'] === 'new' ? null : Number(params['id']);
      this.eventId.set(id);
      this.eventDataLoaded.set(false); // Reset when navigating to different event
    });
  }

  createEventForm(): FormGroup {
    return this.fb.group({
      name: ['', Validators.required],
      description: [''],
      slots: [2, [Validators.required, Validators.min(2)]],
      visibility: [EventVisibility.Public, Validators.required],
      access: [EventAccess.Free, Validators.required],
      startDate: [null],
      endDate: [null, Validators.required],
      registrationEndDate: [null]
    });
  }

  loadEventData(event: UserEvent): void {
    this.eventForm.patchValue({
      name: event.name,
      description: event.description,
      slots: event.slots,
      visibility: event.visibility,
      access: event.access,
      startDate: this.dateToDateTimeLocal(event.startDate),
      endDate: this.dateToDateTimeLocal(event.endDate),
      registrationEndDate: this.dateToDateTimeLocal(event.registrationEndDate)
    });

    // Load content
    if (event.quests && event.quests.length > 0) {
      this.selectedQuest.set(event.quests[0]);
    }
    if (event.obelisks && event.obelisks.length > 0) {
      this.selectedObelisk.set(event.obelisks[0]);
    }
    if (event.compilations && event.compilations.length > 0) {
      this.selectedCompilation.set(event.compilations[0]);
    }

    // Load milestones
    if (event.config?.compilationMilestones) {
      this.milestones.set(event.config.compilationMilestones);
    }

    // Load admins
    if (event.adminsList) {
      this.adminsList.set(event.adminsList);
    }
  }

  /**
   * Convert Date object to datetime-local string format (YYYY-MM-DDTHH:mm)
   */
  private dateToDateTimeLocal(date: Date | null | undefined): string | null {
    if (!date) return null;

    const d = new Date(date);
    if (isNaN(d.getTime())) return null;

    // Format: YYYY-MM-DDTHH:mm
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  /**
   * Convert datetime-local string to Date object
   */
  private dateTimeLocalToDate(dateTimeStr: string | null | undefined): Date | null {
    if (!dateTimeStr) return null;
    const date = new Date(dateTimeStr);
    return isNaN(date.getTime()) ? null : date;
  }

  // Content Selection Methods
  openContentSearchModal(type: 'quest' | 'compilation'): void {
    this.contentSearchType.set(type);
    this.showContentSearchModal.set(true);
    this.contentSearchControl.setValue('');
    this.contentSearchResults.set([]);
  }

  closeContentSearchModal(): void {
    this.showContentSearchModal.set(false);
    this.contentSearchType.set(null);
    this.contentSearchControl.setValue('');
    this.contentSearchResults.set([]);
  }

  selectQuest(quest: QuestMetadata): void {
    this.selectedQuest.set(quest);
    this.selectedObelisk.set(null);
    this.selectedCompilation.set(null);
    if (!this.eventForm.value.name) {
      this.eventForm.patchValue({ name: quest.name });
    }
    this.closeContentSearchModal();
  }

  selectCompilation(compilation: Compilation): void {
    this.selectedCompilation.set(compilation);
    this.selectedQuest.set(null);
    this.selectedObelisk.set(null);
    if (!this.eventForm.value.name) {
      this.eventForm.patchValue({ name: compilation.name });
    }
    this.closeContentSearchModal();
  }

  removeSelectedContent(): void {
    this.selectedQuest.set(null);
    this.selectedObelisk.set(null);
    this.selectedCompilation.set(null);
    this.milestones.set([]);
  }

  // Milestone Management
  addMilestone(): void {
    const currentMilestones = this.milestones();
    if (currentMilestones.length >= 5) {
      this.snackBar.open(
        this.transloco.translate('maximumMilestonesAllowed'),
        'OK',
        { duration: 3000 }
      );
      return;
    }

    this.milestones.set([
      ...currentMilestones,
      { percentage: 0, name: '' }
    ]);
  }

  removeMilestone(index: number): void {
    const currentMilestones = this.milestones();
    this.milestones.set(currentMilestones.filter((_, i) => i !== index));
  }

  updateMilestone(index: number, field: 'percentage' | 'name', value: any): void {
    const currentMilestones = [...this.milestones()];
    currentMilestones[index] = {
      ...currentMilestones[index],
      [field]: field === 'percentage' ? Number(value) : value
    };
    this.milestones.set(currentMilestones);
  }

  // Save Event
  save(): void {
    if (!this.eventForm.valid) {
      this.snackBar.open(
        this.transloco.translate('pleaseFillRequiredFields'),
        'OK',
        { duration: 3000 }
      );
      return;
    }

    if (!this.selectedQuest() && !this.selectedObelisk() && !this.selectedCompilation()) {
      this.snackBar.open(
        this.transloco.translate('pleaseSelectEventContent'),
        'OK',
        { duration: 3000 }
      );
      return;
    }

    const formValue = this.eventForm.value;

    // Determine event type
    let eventType: EventType;
    if (this.selectedQuest()) {
      eventType = EventType.Quest;
    } else if (this.selectedObelisk()) {
      eventType = EventType.Obelisk;
    } else {
      eventType = EventType.Compilation;
    }

    const eventData: UserEvent = {
      name: formValue.name,
      description: formValue.description,
      slots: formValue.slots,
      type: eventType,
      visibility: formValue.visibility,
      access: formValue.access,
      startType: StartType.Manual,
      startDate: this.dateTimeLocalToDate(formValue.startDate),
      endDate: this.dateTimeLocalToDate(formValue.endDate),
      registrationEndDate: this.dateTimeLocalToDate(formValue.registrationEndDate),
      quests: this.selectedQuest() ? [this.selectedQuest()!] : [],
      obelisks: this.selectedObelisk() ? [this.selectedObelisk()!] : [],
      compilations: this.selectedCompilation() ? [this.selectedCompilation()!] : [],
      config: {
        compilationMilestones: this.milestones().length > 0 ? this.milestones() : undefined
      }
    };

    if (!this.isNew()) {
      eventData.id = this.eventId() as number;
    }

    this.isSaving.set(true);

    const saveOperation = this.isNew()
      ? this.eventsService.createEvent$(eventData)
      : this.eventsService.updateEvent$(eventData);

    saveOperation.subscribe({
      next: () => {
        this.isSaving.set(false);
        this.snackBar.open(
          this.transloco.translate(this.isNew() ? 'eventCreatedSuccessfully' : 'eventUpdatedSuccessfully'),
          'OK',
          { duration: 3000 }
        );
        this.router.navigate(['/events']);
      },
      error: (error) => {
        console.error('Error saving event:', error);
        this.isSaving.set(false);
        this.snackBar.open(
          this.transloco.translate('failedToSaveEvent'),
          'OK',
          { duration: 3000 }
        );
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/events']);
  }

  getContentTypeName(): string {
    if (this.selectedQuest()) return 'quest';
    if (this.selectedObelisk()) return 'place';
    if (this.selectedCompilation()) return 'trip';
    return '';
  }

  getContentName(): string {
    if (this.selectedQuest()) return this.selectedQuest()!.name;
    if (this.selectedObelisk()) return this.selectedObelisk()!.name;
    if (this.selectedCompilation()) return this.selectedCompilation()!.name;
    return '';
  }

  isQuest(item: any): item is QuestMetadata {
    return item && 'steps' in item;
  }


  isCompilation(item: any): item is Compilation {
    return item && 'tripConfig' in item;
  }

  // Admin Management Methods
  openAddAdminModal(): void {
    this.showAddAdminModal.set(true);
    this.userSearchControl.setValue('');
    this.searchResults.set([]);
  }

  closeAddAdminModal(): void {
    this.showAddAdminModal.set(false);
    this.userSearchControl.setValue('');
    this.searchResults.set([]);
  }

  addAdmin(user: User): void {
    const eventId = this.eventId();
    if (!eventId || eventId === 'new') {
      this.snackBar.open(
        this.transloco.translate('saveEventBeforeAddingAdmins'),
        'OK',
        { duration: 3000 }
      );
      return;
    }

    // Check if user is already an admin
    if (this.adminsList().some(admin => admin.userId === user.id)) {
      this.snackBar.open(
        this.transloco.translate('userAlreadyAdmin'),
        'OK',
        { duration: 3000 }
      );
      return;
    }

    this.eventsService.addAdmin$(eventId as number, user.id).subscribe({
      next: () => {
        this.snackBar.open(
          this.transloco.translate('adminAddedSuccessfully'),
          'OK',
          { duration: 3000 }
        );
        this.event.reload();
        this.closeAddAdminModal();
      },
      error: (error) => {
        console.error('Error adding admin:', error);
        this.snackBar.open(
          this.transloco.translate('failedToAddAdmin'),
          'OK',
          { duration: 3000 }
        );
      }
    });
  }

  removeAdmin(admin: EventAdmin): void {
    const eventId = this.eventId();
    if (!eventId || eventId === 'new') return;

    this.eventsService.removeAdmin$(eventId as number, admin.id).subscribe({
      next: () => {
        this.snackBar.open(
          this.transloco.translate('adminRemovedSuccessfully'),
          'OK',
          { duration: 3000 }
        );
        this.event.reload();
      },
      error: (error) => {
        console.error('Error removing admin:', error);
        this.snackBar.open(
          this.transloco.translate('failedToRemoveAdmin'),
          'OK',
          { duration: 3000 }
        );
      }
    });
  }
}

